import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import env from '@/env'

// Shape of the stored user info
interface StoredUser {
	_id: string
	email: string
	role: string
	accessProjects: string[]
	accessToken: string
}

interface UserDataPayload {
	_id: string
	email: string
	role: string
	accessProjects: string[]
}

const makeError = (msg: string, err: any): string => {
	console.error('🍒', msg, err)
	return err?.response?.data?.msg || err?.response?.data || err.message || String(err)
}

// Retrieves token and user-payload header data from localStorage
async function getUser(): Promise<{ token: string; data: string }> {
	const raw = localStorage.getItem('auth/user')
	const parsed = raw ? JSON.parse(raw) : {}
	const user: StoredUser = parsed.user || {}

	const token = user.accessToken || ''
	const payload: UserDataPayload = {
		_id: user._id || '',
		email: user.email || '',
		role: user.role || '',
		accessProjects: user.accessProjects || [],
	}

	return { token, data: JSON.stringify(payload) }
}

const instance: AxiosInstance = axios.create({
	baseURL: env.apiUrl,
	responseType: 'json',
	timeout: 300_000, // 300 seconds
})

// Attach auth headers to a config object
async function authConfig(method: string, url: string): Promise<AxiosRequestConfig> {
	const { token, data } = await getUser()
	return {
		headers: {
			Authorization: token || '',
			userdata: data || '',
		},
	}
}

const http = {
	// GET with auth
	async getAuth<T = any>(url: string, _headers = {}): Promise<T> {
		try {
			console.log('GET AUTH', url)
			const config: AxiosRequestConfig = await authConfig('GET', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.get<T>(url, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.getAuth ${url}`, err)
			throw new Error(msg)
		}
	},

	// Simple GET
	async get<T = any>(url: string, _headers: {} = {}): Promise<T> {
		try {
			console.log('GET', url)
			const config: AxiosRequestConfig = await authConfig('GET', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.get<T>(url, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.get ${url}`, err)
			throw new Error(msg)
		}
	},

	// POST with auth and generic response
	async postAuth<T = any, B = any>(url: string, body: B, _headers: {} = {}): Promise<T> {
		try {
			console.log('POST AUTH', url)
			const config = await authConfig('POST', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.post<T>(url, body, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.postAuth ${url}`, err)
			throw new Error(msg)
		}
	},

	// Simple POST
	async post<T = any, B = any>(url: string, body: B, _headers: {} = {}): Promise<T> {
		try {
			console.log('POST', url)
			const config = await authConfig('POST', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.post<T>(url, body, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.post ${url}`, err)
			throw new Error(msg)
		}
	},

	// DELETE with auth
	async deleteAuth<T = any>(url: string): Promise<T> {
		try {
			console.log('DELETE AUTH', url)
			const config = await authConfig('DELETE', url)
			const resp = await instance.delete<T>(url, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.deleteAuth ${url}`, err)
			throw new Error(msg)
		}
	},

	// PUT with auth
	async putAuth<T = any, B = any>(url: string, body: B): Promise<T> {
		try {
			console.log('PUT AUTH', url)
			const config = await authConfig('PUT', url)
			const resp = await instance.put<T>(url, body, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.putAuth ${url}`, err)
			throw new Error(msg)
		}
	},
}

export default http
